import React, { useState, useEffect } from 'react';
import { 
  X, 
  Plus, 
  Trash2, 
  ArrowLeft,
  Eye,
  EyeOff,
  Archive,
  Save,
  Shirt,
  Sparkles,
  Check,
  GripVertical,
  Loader2,
  ZoomIn,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { stylistAPI } from '../../../../services/api';
import LookDetailView from '../../../../components/LookDetailView';

const SEASONS = [
  { value: 'лето', label: 'Лето' },
  { value: 'зима', label: 'Зима' },
  { value: 'межсезонье', label: 'Межсезонье' },
  { value: 'круглогодичная', label: 'Круглогодичная' },
];

const CapsuleDetailModal = ({ 
  capsuleId, 
  clientId,
  allWardrobeItems,
  allLooks,
  onClose, 
  onUpdated,
  onCreateLook
}) => {
  const [capsule, setCapsule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('looks');
  const [showItemSelector, setShowItemSelector] = useState(false);
  const [showLookSelector, setShowLookSelector] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  const [formData, setFormData] = useState({});
  const [eventInput, setEventInput] = useState('');
  const [viewingImage, setViewingImage] = useState(null);
  const [viewingLook, setViewingLook] = useState(null);

  useEffect(() => {
    fetchCapsule();
  }, [capsuleId]);

  const fetchCapsule = async () => {
    try {
      const data = await stylistAPI.getCapsuleDetail(capsuleId);
      setCapsule(data);
      setFormData({
        name: data.name,
        season: data.season,
        description: data.description || '',
        event_types: data.event_types || []
      });
    } catch (err) {
      console.error('Error fetching capsule:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDetails = async () => {
    setSaving(true);
    try {
      await stylistAPI.updateCapsule(capsuleId, formData);
      setCapsule({ ...capsule, ...formData });
      setEditingDetails(false);
      onUpdated();
    } catch (err) {
      console.error('Error updating capsule:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleAddItems = async (itemIds) => {
    try {
      await stylistAPI.addItemsToCapsule(capsuleId, itemIds);
      fetchCapsule();
      setShowItemSelector(false);
      onUpdated();
    } catch (err) {
      console.error('Error adding items:', err);
    }
  };

  const handleRemoveItem = async (itemId) => {
    if (!window.confirm('Убрать эту вещь из капсулы?')) return;
    try {
      await stylistAPI.removeItemFromCapsule(capsuleId, itemId);
      setCapsule({
        ...capsule,
        wardrobe_items: capsule.wardrobe_items.filter(i => i.id !== itemId)
      });
      onUpdated();
    } catch (err) {
      console.error('Error removing item:', err);
    }
  };

  const handleAddLooks = async (lookIds) => {
    try {
      await stylistAPI.addLooksToCapsule(capsuleId, lookIds);
      fetchCapsule();
      setShowLookSelector(false);
      onUpdated();
    } catch (err) {
      console.error('Error adding looks:', err);
    }
  };

  const handleRemoveLook = async (lookId) => {
    if (!window.confirm('Убрать этот образ из капсулы?')) return;
    try {
      await stylistAPI.removeLookFromCapsule(capsuleId, lookId);
      setCapsule({
        ...capsule,
        looks: capsule.looks.filter(l => l.id !== lookId)
      });
      onUpdated();
    } catch (err) {
      console.error('Error removing look:', err);
    }
  };

  const handlePublish = async () => {
    try {
      await stylistAPI.publishCapsule(capsuleId);
      setCapsule({ ...capsule, status: 'published' });
      onUpdated();
    } catch (err) {
      console.error('Error publishing:', err);
    }
  };

  const handleArchive = async () => {
    try {
      await stylistAPI.archiveCapsule(capsuleId);
      setCapsule({ ...capsule, status: 'archived' });
      onUpdated();
    } catch (err) {
      console.error('Error archiving:', err);
    }
  };

  const handleSetDraft = async () => {
    try {
      await stylistAPI.setCapsuleDraft(capsuleId);
      setCapsule({ ...capsule, status: 'draft' });
      onUpdated();
    } catch (err) {
      console.error('Error setting draft:', err);
    }
  };

  const handleAddEvent = () => {
    if (eventInput.trim() && !formData.event_types.includes(eventInput.trim())) {
      setFormData({ ...formData, event_types: [...formData.event_types, eventInput.trim()] });
      setEventInput('');
    }
  };

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <Loader2 size={32} style={{ color: 'var(--accent-gold)', animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!capsule) return null;

  const capsuleItemIds = capsule.wardrobe_items?.map(i => i.id) || [];
  const capsuleLookIds = capsule.looks?.map(l => l.id) || [];
  const availableItems = allWardrobeItems.filter(i => !capsuleItemIds.includes(i.id));
  const availableLooks = allLooks.filter(l => !capsuleLookIds.includes(l.id));

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'var(--cream)',
      zIndex: 1000,
      overflow: 'auto'
    }}>
      {/* Header */}
      <div style={{
        backgroundColor: 'var(--white)',
        borderBottom: '1px solid var(--soft-gray)',
        padding: 'var(--space-l) var(--space-xl)',
        position: 'sticky',
        top: 0,
        zIndex: 10
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 'var(--space-m)'
        }}>
          <button
            onClick={onClose}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-s)',
              background: 'none',
              border: 'none',
              fontFamily: 'var(--font-body)',
              fontSize: '0.875rem',
              color: 'var(--mid-gray)',
              cursor: 'pointer',
              padding: 0
            }}
          >
            <ArrowLeft size={16} />
            Назад к капсулам
          </button>

          <div style={{ display: 'flex', gap: 'var(--space-s)', flexWrap: 'wrap' }}>
            {/* Status indicator */}
            <span style={{
              padding: '8px 16px',
              backgroundColor: capsule.status === 'published' ? 'rgba(143,143,126,0.1)' : 'var(--cream)',
              fontSize: '0.75rem',
              fontFamily: 'var(--font-body)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              color: capsule.status === 'published' ? 'var(--warm-sage)' : 'var(--mid-gray)'
            }}>
              {capsule.status === 'draft' && 'Черновик'}
              {capsule.status === 'published' && 'Опубликовано'}
              {capsule.status === 'archived' && 'В архиве'}
            </span>

            {capsule.status === 'draft' && (
              <button
                onClick={handlePublish}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-xs)',
                  padding: '8px 16px',
                  backgroundColor: 'var(--warm-sage)',
                  color: 'var(--white)',
                  border: 'none',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.8125rem',
                  cursor: 'pointer'
                }}
              >
                <Eye size={14} />
                Опубликовать
              </button>
            )}

            {capsule.status === 'published' && (
              <>
                <button
                  onClick={handleSetDraft}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'var(--cream)',
                    border: 'none',
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.8125rem',
                    cursor: 'pointer'
                  }}
                >
                  В черновик
                </button>
                <button
                  onClick={handleArchive}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-xs)',
                    padding: '8px 16px',
                    backgroundColor: 'var(--cream)',
                    border: 'none',
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.8125rem',
                    cursor: 'pointer'
                  }}
                >
                  <Archive size={14} />
                  Архивировать
                </button>
              </>
            )}

            {capsule.status === 'archived' && (
              <button
                onClick={handleSetDraft}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'var(--cream)',
                  border: 'none',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.8125rem',
                  cursor: 'pointer'
                }}
              >
                Восстановить
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: 'var(--space-xl)' }}>
        {/* Capsule Info */}
        <div style={{
          backgroundColor: 'var(--white)',
          padding: 'var(--space-xl)',
          marginBottom: 'var(--space-xl)',
          border: '1px solid var(--soft-gray)'
        }}>
          {editingDetails ? (
            <div>
              <div style={{ marginBottom: 'var(--space-l)' }}>
                <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: '0.75rem', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--mid-gray)', marginBottom: 'var(--space-s)' }}>
                  Название *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  style={{ width: '100%', padding: '12px', fontFamily: 'var(--font-body)', fontSize: '1rem', border: '1px solid var(--soft-gray)', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ marginBottom: 'var(--space-l)' }}>
                <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: '0.75rem', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--mid-gray)', marginBottom: 'var(--space-s)' }}>
                  Сезон
                </label>
                <div style={{ display: 'flex', gap: 'var(--space-s)', flexWrap: 'wrap' }}>
                  {SEASONS.map((season) => (
                    <button
                      key={season.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, season: season.value })}
                      style={{
                        padding: '8px 14px',
                        backgroundColor: formData.season === season.value ? 'var(--black)' : 'var(--cream)',
                        color: formData.season === season.value ? 'var(--white)' : 'var(--charcoal)',
                        border: 'none',
                        fontFamily: 'var(--font-body)',
                        fontSize: '0.8125rem',
                        cursor: 'pointer'
                      }}
                    >
                      {season.label}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 'var(--space-l)' }}>
                <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: '0.75rem', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--mid-gray)', marginBottom: 'var(--space-s)' }}>
                  Описание
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  style={{ width: '100%', padding: '12px', fontFamily: 'var(--font-body)', fontSize: '0.9375rem', border: '1px solid var(--soft-gray)', boxSizing: 'border-box', resize: 'vertical' }}
                />
              </div>

              <div style={{ marginBottom: 'var(--space-l)' }}>
                <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: '0.75rem', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--mid-gray)', marginBottom: 'var(--space-s)' }}>
                  События
                </label>
                <div style={{ display: 'flex', gap: 'var(--space-s)', marginBottom: 'var(--space-s)' }}>
                  <input
                    type="text"
                    value={eventInput}
                    onChange={(e) => setEventInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddEvent())}
                    placeholder="офис, свидание..."
                    style={{ flex: 1, padding: '10px', fontFamily: 'var(--font-body)', fontSize: '0.875rem', border: '1px solid var(--soft-gray)' }}
                  />
                  <button type="button" onClick={handleAddEvent} style={{ padding: '10px 16px', backgroundColor: 'var(--cream)', border: 'none', cursor: 'pointer' }}>
                    <Plus size={16} />
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-s)', flexWrap: 'wrap' }}>
                  {formData.event_types.map((event, idx) => (
                    <span key={idx} style={{ padding: '4px 10px', backgroundColor: 'var(--cream)', fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {event}
                      <button type="button" onClick={() => setFormData({ ...formData, event_types: formData.event_types.filter((_, i) => i !== idx) })} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 'var(--space-m)' }}>
                <button
                  onClick={handleSaveDetails}
                  disabled={saving}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-xs)',
                    padding: '12px 24px',
                    backgroundColor: 'var(--black)',
                    color: 'var(--white)',
                    border: 'none',
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.8125rem',
                    cursor: saving ? 'not-allowed' : 'pointer'
                  }}
                >
                  <Save size={14} />
                  {saving ? 'Сохранение...' : 'Сохранить'}
                </button>
                <button
                  onClick={() => {
                    setEditingDetails(false);
                    setFormData({
                      name: capsule.name,
                      season: capsule.season,
                      description: capsule.description || '',
                      event_types: capsule.event_types || []
                    });
                  }}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: 'var(--cream)',
                    border: 'none',
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.8125rem',
                    cursor: 'pointer'
                  }}
                >
                  Отмена
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-m)' }}>
                <div>
                  <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.5rem, 3vw, 2rem)', fontWeight: 300, marginBottom: 'var(--space-xs)' }}>
                    {capsule.name}
                  </h1>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: '0.75rem', color: 'var(--light-gray)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {capsule.season}
                  </p>
                </div>
                <button
                  onClick={() => setEditingDetails(true)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'var(--cream)',
                    border: 'none',
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.8125rem',
                    cursor: 'pointer'
                  }}
                >
                  Изменить
                </button>
              </div>

              {capsule.description && (
                <p style={{ fontFamily: 'var(--font-body)', fontSize: '0.9375rem', color: 'var(--mid-gray)', marginBottom: 'var(--space-m)', lineHeight: 1.6 }}>
                  {capsule.description}
                </p>
              )}

              {capsule.event_types?.length > 0 && (
                <div style={{ display: 'flex', gap: 'var(--space-s)', flexWrap: 'wrap' }}>
                  {capsule.event_types.map((event, idx) => (
                    <span key={idx} style={{ padding: '4px 12px', backgroundColor: 'var(--cream)', fontSize: '0.8125rem', fontFamily: 'var(--font-body)' }}>
                      {event}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 'var(--space-s)', marginBottom: 'var(--space-l)' }}>
          <button
            onClick={() => setActiveTab('looks')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-s)',
              padding: '12px 20px',
              backgroundColor: activeTab === 'looks' ? 'var(--black)' : 'var(--white)',
              color: activeTab === 'looks' ? 'var(--white)' : 'var(--charcoal)',
              border: '1px solid var(--soft-gray)',
              fontFamily: 'var(--font-body)',
              fontSize: '0.8125rem',
              cursor: 'pointer'
            }}
          >
            <Sparkles size={16} />
            Образы
            <span style={{
              backgroundColor: activeTab === 'looks' ? 'var(--accent-gold)' : 'var(--cream)',
              color: activeTab === 'looks' ? 'var(--black)' : 'var(--charcoal)',
              padding: '2px 8px',
              borderRadius: '10px',
              fontSize: '0.75rem'
            }}>
              {capsule.looks?.length || 0}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('items')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-s)',
              padding: '12px 20px',
              backgroundColor: activeTab === 'items' ? 'var(--black)' : 'var(--white)',
              color: activeTab === 'items' ? 'var(--white)' : 'var(--charcoal)',
              border: '1px solid var(--soft-gray)',
              fontFamily: 'var(--font-body)',
              fontSize: '0.8125rem',
              cursor: 'pointer'
            }}
          >
            <Shirt size={16} />
            Вещи
            <span style={{
              backgroundColor: activeTab === 'items' ? 'var(--accent-gold)' : 'var(--cream)',
              color: activeTab === 'items' ? 'var(--black)' : 'var(--charcoal)',
              padding: '2px 8px',
              borderRadius: '10px',
              fontSize: '0.75rem'
            }}>
              {capsule.wardrobe_items?.length || 0}
            </span>
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'looks' && (
          <div style={{ backgroundColor: 'var(--white)', border: '1px solid var(--soft-gray)', padding: 'var(--space-xl)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-l)' }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.25rem', fontWeight: 400 }}>Образы в капсуле</h3>
              <div style={{ display: 'flex', gap: 'var(--space-s)' }}>
                <button
                  onClick={() => setShowLookSelector(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-xs)',
                    padding: '8px 16px',
                    backgroundColor: 'var(--cream)',
                    border: 'none',
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.8125rem',
                    cursor: 'pointer'
                  }}
                >
                  <Plus size={14} />
                  Добавить существующий
                </button>
                <button
                  onClick={() => onCreateLook(capsuleId)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-xs)',
                    padding: '8px 16px',
                    backgroundColor: 'var(--black)',
                    color: 'var(--white)',
                    border: 'none',
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.8125rem',
                    cursor: 'pointer'
                  }}
                >
                  <Plus size={14} />
                  Создать новый
                </button>
              </div>
            </div>

            {capsule.looks?.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 'var(--space-xxl)', color: 'var(--mid-gray)' }}>
                <Sparkles size={32} style={{ marginBottom: 'var(--space-m)', opacity: 0.3 }} />
                <p>Нет образов в капсуле</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 'var(--space-l)' }}>
                {capsule.looks?.map((look) => (
                  <div key={look.id} style={{ position: 'relative' }}>
                    {look.deleted ? (
                      <div style={{ height: '200px', backgroundColor: 'var(--cream)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--light-gray)' }}>
                        Образ удалён
                      </div>
                    ) : (
                      <>
                        <div 
                          style={{ height: '200px', backgroundColor: 'var(--cream)', overflow: 'hidden', cursor: 'pointer' }}
                          onClick={() => setViewingLook(look)}
                        >
                          {look.look_images?.[0] ? (
                            <img src={look.look_images[0]} alt={look.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Sparkles size={32} style={{ color: 'var(--light-gray)' }} />
                            </div>
                          )}
                        </div>
                        <div style={{ padding: 'var(--space-m)', cursor: 'pointer' }} onClick={() => setViewingLook(look)}>
                          <p style={{ fontFamily: 'var(--font-body)', fontSize: '0.875rem', marginBottom: 'var(--space-xs)' }}>{look.name}</p>
                          {look.event_types?.length > 0 && (
                            <p style={{ fontSize: '0.75rem', color: 'var(--light-gray)' }}>{look.event_types.join(', ')}</p>
                          )}
                        </div>
                      </>
                    )}
                    <button
                      onClick={() => handleRemoveLook(look.id)}
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        padding: '6px',
                        backgroundColor: 'rgba(255,255,255,0.9)',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        color: '#ef4444'
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'items' && (
          <div style={{ backgroundColor: 'var(--white)', border: '1px solid var(--soft-gray)', padding: 'var(--space-xl)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-l)' }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.25rem', fontWeight: 400 }}>Вещи в капсуле</h3>
              <button
                onClick={() => setShowItemSelector(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-xs)',
                  padding: '8px 16px',
                  backgroundColor: 'var(--black)',
                  color: 'var(--white)',
                  border: 'none',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.8125rem',
                  cursor: 'pointer'
                }}
              >
                <Plus size={14} />
                Добавить вещи
              </button>
            </div>

            {capsule.wardrobe_items?.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 'var(--space-xxl)', color: 'var(--mid-gray)' }}>
                <Shirt size={32} style={{ marginBottom: 'var(--space-m)', opacity: 0.3 }} />
                <p>Нет вещей в капсуле</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 'var(--space-m)' }}>
                {capsule.wardrobe_items?.map((item) => (
                  <div key={item.id} style={{ position: 'relative' }}>
                    {item.deleted ? (
                      <div style={{ height: '150px', backgroundColor: 'var(--cream)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--light-gray)', fontSize: '0.75rem' }}>
                        Вещь удалена
                      </div>
                    ) : (
                      <>
                        <div 
                          style={{ height: '150px', backgroundColor: 'var(--cream)', overflow: 'hidden', cursor: 'pointer' }}
                          onClick={() => item.image_url && setViewingImage(item.image_url)}
                        >
                          {item.image_url ? (
                            <img src={item.image_url} alt={item.item_kind} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Shirt size={24} style={{ color: 'var(--light-gray)' }} />
                            </div>
                          )}
                        </div>
                        <p style={{ fontFamily: 'var(--font-body)', fontSize: '0.75rem', textAlign: 'center', marginTop: 'var(--space-xs)', textTransform: 'capitalize' }}>
                          {item.item_kind}
                        </p>
                      </>
                    )}
                    <button
                      onClick={() => handleRemoveItem(item.id)}
                      style={{
                        position: 'absolute',
                        top: '6px',
                        right: '6px',
                        padding: '4px',
                        backgroundColor: 'rgba(255,255,255,0.9)',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        color: '#ef4444'
                      }}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Item Selector Modal */}
      {showItemSelector && (
        <ItemSelectorModal
          items={availableItems}
          onSelect={handleAddItems}
          onClose={() => setShowItemSelector(false)}
        />
      )}

      {/* Look Selector Modal */}
      {showLookSelector && (
        <LookSelectorModal
          looks={availableLooks}
          onSelect={handleAddLooks}
          onClose={() => setShowLookSelector(false)}
        />
      )}

      {/* Look Detail View */}
      {viewingLook && (
        <LookDetailView
          look={viewingLook}
          wardrobeItems={allWardrobeItems}
          onClose={() => setViewingLook(null)}
          onImageClick={(img) => setViewingImage(img)}
        />
      )}

      {/* Image Viewer */}
      {viewingImage && (
        <div
          onClick={() => setViewingImage(null)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            cursor: 'zoom-out'
          }}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setViewingImage(null); }}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              color: 'white',
              padding: '12px',
              cursor: 'pointer',
              borderRadius: '50%'
            }}
          >
            <X size={24} />
          </button>
          <img 
            src={viewingImage} 
            alt="" 
            style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain' }}
          />
        </div>
      )}
    </div>
  );
};


// Item Selector Sub-component
const ItemSelectorModal = ({ items, onSelect, onClose }) => {
  const [selected, setSelected] = useState([]);
  const [filter, setFilter] = useState('');

  const filteredItems = items.filter(item => 
    item.item_kind?.toLowerCase().includes(filter.toLowerCase()) ||
    item.item_type?.toLowerCase().includes(filter.toLowerCase())
  );

  const toggleItem = (id) => {
    setSelected(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  return (
    <div 
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-l)',
        zIndex: 1100
      }}
    >
      <div 
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'var(--white)',
          maxWidth: '700px',
          width: '100%',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <div style={{ padding: 'var(--space-l)', borderBottom: '1px solid var(--soft-gray)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.25rem', fontWeight: 400 }}>Выбрать вещи</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: 'var(--space-m) var(--space-l)', borderBottom: '1px solid var(--soft-gray)' }}>
          <input
            type="text"
            placeholder="Поиск по названию или типу..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--soft-gray)', fontFamily: 'var(--font-body)', fontSize: '0.875rem' }}
          />
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 'var(--space-l)' }}>
          {filteredItems.length === 0 ? (
            <p style={{ textAlign: 'center', color: 'var(--mid-gray)', padding: 'var(--space-xl)' }}>
              {items.length === 0 ? 'Все вещи уже добавлены' : 'Ничего не найдено'}
            </p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 'var(--space-m)' }}>
              {filteredItems.map((item) => (
                <div 
                  key={item.id}
                  onClick={() => toggleItem(item.id)}
                  style={{ cursor: 'pointer', position: 'relative' }}
                >
                  <div style={{
                    height: '100px',
                    backgroundColor: 'var(--cream)',
                    overflow: 'hidden',
                    border: selected.includes(item.id) ? '3px solid var(--accent-gold)' : '3px solid transparent'
                  }}>
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.item_kind} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Shirt size={20} style={{ color: 'var(--light-gray)' }} />
                      </div>
                    )}
                  </div>
                  {selected.includes(item.id) && (
                    <div style={{
                      position: 'absolute',
                      top: '6px',
                      right: '6px',
                      width: '20px',
                      height: '20px',
                      backgroundColor: 'var(--accent-gold)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Check size={12} style={{ color: 'var(--black)' }} />
                    </div>
                  )}
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: '0.6875rem', textAlign: 'center', marginTop: '4px', textTransform: 'capitalize' }}>
                    {item.item_kind}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ padding: 'var(--space-l)', borderTop: '1px solid var(--soft-gray)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.875rem', color: 'var(--mid-gray)' }}>
            Выбрано: {selected.length}
          </span>
          <div style={{ display: 'flex', gap: 'var(--space-m)' }}>
            <button onClick={onClose} style={{ padding: '10px 20px', backgroundColor: 'var(--cream)', border: 'none', fontFamily: 'var(--font-body)', cursor: 'pointer' }}>
              Отмена
            </button>
            <button 
              onClick={() => onSelect(selected)}
              disabled={selected.length === 0}
              style={{ 
                padding: '10px 20px', 
                backgroundColor: selected.length > 0 ? 'var(--black)' : 'var(--soft-gray)', 
                color: 'var(--white)', 
                border: 'none', 
                fontFamily: 'var(--font-body)', 
                cursor: selected.length > 0 ? 'pointer' : 'not-allowed' 
              }}
            >
              Добавить
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


// Look Selector Sub-component
const LookSelectorModal = ({ looks, onSelect, onClose }) => {
  const [selected, setSelected] = useState([]);

  const toggleLook = (id) => {
    setSelected(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  return (
    <div 
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-l)',
        zIndex: 1100
      }}
    >
      <div 
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'var(--white)',
          maxWidth: '700px',
          width: '100%',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <div style={{ padding: 'var(--space-l)', borderBottom: '1px solid var(--soft-gray)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.25rem', fontWeight: 400 }}>Выбрать образы</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 'var(--space-l)' }}>
          {looks.length === 0 ? (
            <p style={{ textAlign: 'center', color: 'var(--mid-gray)', padding: 'var(--space-xl)' }}>
              Все образы уже добавлены или нет доступных образов
            </p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 'var(--space-m)' }}>
              {looks.map((look) => (
                <div 
                  key={look.id}
                  onClick={() => toggleLook(look.id)}
                  style={{ cursor: 'pointer', position: 'relative' }}
                >
                  <div style={{
                    height: '150px',
                    backgroundColor: 'var(--cream)',
                    overflow: 'hidden',
                    border: selected.includes(look.id) ? '3px solid var(--accent-gold)' : '3px solid transparent'
                  }}>
                    {look.look_images?.[0] ? (
                      <img src={look.look_images[0]} alt={look.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Sparkles size={24} style={{ color: 'var(--light-gray)' }} />
                      </div>
                    )}
                  </div>
                  {selected.includes(look.id) && (
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      width: '24px',
                      height: '24px',
                      backgroundColor: 'var(--accent-gold)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Check size={14} style={{ color: 'var(--black)' }} />
                    </div>
                  )}
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: '0.75rem', textAlign: 'center', marginTop: '6px' }}>
                    {look.name}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ padding: 'var(--space-l)', borderTop: '1px solid var(--soft-gray)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.875rem', color: 'var(--mid-gray)' }}>
            Выбрано: {selected.length}
          </span>
          <div style={{ display: 'flex', gap: 'var(--space-m)' }}>
            <button onClick={onClose} style={{ padding: '10px 20px', backgroundColor: 'var(--cream)', border: 'none', fontFamily: 'var(--font-body)', cursor: 'pointer' }}>
              Отмена
            </button>
            <button 
              onClick={() => onSelect(selected)}
              disabled={selected.length === 0}
              style={{ 
                padding: '10px 20px', 
                backgroundColor: selected.length > 0 ? 'var(--black)' : 'var(--soft-gray)', 
                color: 'var(--white)', 
                border: 'none', 
                fontFamily: 'var(--font-body)', 
                cursor: selected.length > 0 ? 'pointer' : 'not-allowed' 
              }}
            >
              Добавить
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CapsuleDetailModal;
